import * as Yup from "yup";
import { config_variable } from '../helper/commonApi';
import { FORMULA_VARIABLES } from './json';

/**
 * Extracts the date portion (YYYY-MM-DD) from an ISO 8601 string.
 * @param {string} isoDate - The ISO 8601 date string.
 * @returns {string} The formatted date string in YYYY-MM-DD format.
*/
export const extractDate = (isoDate, type = true) => {
    const date = new Date(isoDate);
    const options = type
        ? {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true,
        }
        : {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        };
    return date.toLocaleString('en-IN', options);
};

/**
 * Calculates the last and next update dates based on the current date, interval value, and unit.
 *
 * @param {Date} currentDate - The current date.
 * @param {number} intervalValue - The number of units to add.
 * @param {string} intervalUnit - The unit of time ('days', 'months', 'years', 'minutes', 'seconds').
 * @returns {Object} - An object containing the last and next update dates.
*/
export const calculateUpdateDates = (currentDate, intervalValue, intervalUnit) => {
    const lastUpdateDate = new Date(currentDate);
    const nextUpdateDate = new Date(currentDate);

    switch (intervalUnit) {
        case 'days':
            nextUpdateDate.setDate(nextUpdateDate.getDate() + Number(intervalValue));
            break;
        case 'months':
            nextUpdateDate.setMonth(nextUpdateDate.getMonth() + Number(intervalValue));
            break;
        case 'years':
            nextUpdateDate.setFullYear(nextUpdateDate.getFullYear() + Number(intervalValue));
            break;
        case 'hours':
            nextUpdateDate.setHours(nextUpdateDate.getHours() + Number(intervalValue));
            break;
        case 'minutes':
            nextUpdateDate.setMinutes(nextUpdateDate.getMinutes() + Number(intervalValue));
            break;
        default:
            console.error('Unsupported interval unit');
            return null;
    }

    return {
        lastUpdate: lastUpdateDate.toISOString(),
        nextUpdate: nextUpdateDate.toISOString(),
    };
};

export const validationSchema = Yup.object({
    email: Yup.string().email('Please enter a valid email address').required('Email is required'),
    phone: Yup.string().matches(/^[0-9]+$/, 'Phone number must contain only numbers').max(15, 'Phone number cannot exceed 15 digits').required('Phone is required'),
    message: Yup.string().required('Message is required')
});

export const dynamicResourceName = (name) => {
    return {
        singular: name,
        plural: name + 's',
    }
};

export const isAdmin = window?.shopify?.origin?.includes('admin.shopify.com');

export const redirectToAdmin = (type, productId, variantId, collectionId) => {
    let url = 'https://admin.shopify.com/store/';
    if (type === 'products') {
        url += `${config_variable?.shop_name?.split('.')[0]}/${type}/${productId}`;
    } else if (type === 'variants') {
        url += `${config_variable?.shop_name?.split('.')[0]}/products/${productId}/${type}/${variantId}`;
    } else if (type === 'collections') {
        url += `${config_variable?.shop_name?.split('.')[0]}/${type}/${collectionId}`;
    }
    window.open(url, '_blank');
};

export const limit = 5;

export const dateFormat = (date) => {
    const formattedDate = date ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' }) : 'N/A';
    return formattedDate;
};
/**
 * Turns a metafield key into the name used inside a formula, so custom.making_charge
 * is makingCharge and a store's own custom.diamond_price is diamondPrice.
 * Mirrors toFormulaVariable in server/src/utils/common.js.
 *
 * @param {string} key - The metafield key.
 * @returns {string} The formula variable name.
*/
export const toFormulaVariable = (key = '') =>
    String(key).trim().replace(/[_-]+([a-z0-9])/gi, (match, character) => character.toUpperCase());

/* Math is not a variable, it is the one global a formula may call, so a store
   metafield named "Math" must never be substituted. Mirrors the server. */
export const RESERVED_FORMULA_NAMES = ['Math'];

/**
 * Validates a custom pricing formula before it is saved.
 * Mirrors evaluateCustomFormula in server/src/utils/common.js: known variables are
 * substituted, and whatever remains must be numbers, operators and parentheses.
 * Without this a typo like "fixedcharge" saves fine and then silently prices at 0.
 *
 * @param {string} formula - The formula the merchant typed.
 * @param {string[]} [variableNames] - Names this store may use, standard set by default.
 * @returns {string} An error message, or an empty string when the formula is valid.
*/
export const validateCustomFormula = (formula, variableNames = FORMULA_VARIABLES) => {
    if (!formula || formula.trim() === '') {
        return 'Formula is required.';
    }
    /* Longest first so basePrice is never consumed by base, matching the server. */
    const names = [...new Set([...variableNames, 'base'])]
        .filter((name) => /^[A-Za-z][A-Za-z0-9]*$/.test(name) && !RESERVED_FORMULA_NAMES.includes(name))
        .sort((a, b) => b.length - a.length);
    const expression = formula.replace(new RegExp(`\\b(${names.join('|')})\\b`, 'g'), '1');
    /* Collapse the allowed Math calls to a plain bracket before looking for stray
       names, so Math.round passes but Math.constructor and typos still fail. */
    const probe = expression.replace(/\bMath\.(?:round|ceil|floor|abs)\s*\(/g, '(');
    const unknown = probe.match(/[a-zA-Z_][a-zA-Z0-9_.]*/g);
    if (unknown) {
        return `Unknown name: ${[...new Set(unknown)].join(', ')}. You can use ${variableNames.join(', ')}, and Math.round/ceil/floor/abs.`;
    }
    if (!/^[0-9+\-*/%.()\s]+$/.test(probe)) {
        return 'Formula can only contain variables, numbers, and the operators + - * / % ( ).';
    }
    try {
        // eslint-disable-next-line no-new-func
        const result = new Function(`return (${expression});`)();
        if (!Number.isFinite(result)) {
            return 'Formula does not evaluate to a number.';
        }
    } catch (err) {
        return 'Formula is not a valid expression. Check your brackets and operators.';
    }
    return '';
};
